import React, { useEffect, useState } from "react";
import { api, fmtBRL, STATUS_LABELS, STATUS_CLASS, PLAN_LABELS, fmtDate } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendUp, Coins, FileText, Warning, CheckCircle, UsersThree, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const COLORS = ["#1E2A44", "#D97706", "#DC2626", "#0891B2", "#7C3AED"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    api.get("/stats/admin").then((r) => setStats(r.data));
    api.get("/contracts").then((r) => setContracts(r.data));
  }, [seeded]);

  const seed = async () => {
    try {
      await api.post("/dev/seed-demo");
      setSeeded((s) => !s);
    } catch {}
  };

  if (!stats) return <div className="p-10 text-slate-500">Carregando painel…</div>;

  const empty = stats.total_contracts === 0 && stats.clients_count === 0;

  const pieData = Object.entries(stats.status_breakdown).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k, value: v,
  })).filter((d) => d.value > 0);

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="eyebrow mb-1">Painel Imobiliária</div>
          <h1 className="font-display text-4xl tracking-tight">Visão Geral</h1>
        </div>
        <div className="text-sm text-slate-500">Atualizado {new Date().toLocaleString("pt-BR")}</div>
      </div>

      {empty && (
        <div className="card-flat p-8 mb-6 flex items-center justify-between bg-[var(--emerald-soft)] border-[var(--emerald)]/20" data-testid="empty-cta">
          <div>
            <div className="font-display text-2xl">Ainda sem contratos?</div>
            <div className="text-sm text-slate-600 mt-1">Comece cadastrando um cliente ou carregue dados de exemplo para explorar.</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={seed} data-testid="load-demo-btn">Carregar demo</Button>
            <Link to="/clients"><Button className="btn-primary">Cadastrar Cliente <ArrowRight size={14} className="ml-2" /></Button></Link>
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Metric icon={UsersThree} label="Clientes" value={stats.clients_count} testId="metric-clients" />
        <Metric icon={FileText} label="Contratos Ativos" value={stats.active_contracts} sub={`${stats.total_contracts} totais`} testId="metric-active" />
        <Metric icon={Coins} label="GMV Mensal" value={fmtBRL(stats.gmv)} sub={`Comissões ${fmtBRL(stats.commissions)}`} testId="metric-gmv" />
        <Metric icon={CheckCircle} label="Prêmios Garantia" value={fmtBRL(stats.premiums)} sub="MRR estimado" tone="success" testId="metric-premiums" />
        <Metric icon={Warning} label="Inadimplência" value={`${stats.default_rate}%`} sub={`${stats.default_contracts} contratos`} tone={stats.default_rate > 10 ? "danger" : "neutral"} testId="metric-default" />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="card-flat p-6 lg:col-span-2" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Últimos 6 meses</div>
              <div className="font-display text-xl mt-1">Faturamento</div>
            </div>
            <div className="status-pill status-ativo"><TrendUp size={12} /> +18% MoM</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.monthly}>
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ background: "#fff", border: "1px solid #E7E5E0", borderRadius: 6 }} />
              <Bar dataKey="receita" fill="#1E2A44" radius={[4,4,0,0]} />
              <Bar dataKey="premios" fill="#D97706" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card-flat p-6" data-testid="status-chart">
          <div className="eyebrow">Status</div>
          <div className="font-display text-xl mt-1 mb-3">Distribuição</div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend verticalAlign="bottom" height={36} iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-56 flex items-center justify-center text-slate-400 text-sm">Sem contratos ainda</div>
          )}
        </div>
      </div>

      {/* Recent contracts */}
      <div className="card-flat p-6" data-testid="recent-contracts">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Últimos contratos</div>
            <div className="font-display text-xl mt-1">Movimentações recentes</div>
          </div>
          <Link to="/contracts"><Button variant="outline" size="sm">Ver todos <ArrowRight size={12} className="ml-1" /></Button></Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 eyebrow">
                <th className="py-2">Inquilino</th>
                <th className="py-2">Imóvel</th>
                <th className="py-2">Plano</th>
                <th className="py-2">Aluguel</th>
                <th className="py-2">Status</th>
                <th className="py-2">Criado</th>
              </tr>
            </thead>
            <tbody>
              {contracts.slice(0, 8).map((c) => (
                <tr key={c.contract_id} className="border-t border-[var(--line)]">
                  <td className="py-3 font-medium">{c.tenant_name}</td>
                  <td className="py-3 text-slate-600 max-w-xs truncate">{c.property_address}</td>
                  <td className="py-3">{PLAN_LABELS[c.plan]}</td>
                  <td className="py-3 mono">{fmtBRL(c.rent_amount)}</td>
                  <td className="py-3"><span className={`status-pill ${STATUS_CLASS[c.status]}`}>{STATUS_LABELS[c.status]}</span></td>
                  <td className="py-3 text-slate-500">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
              {contracts.length === 0 && (
                <tr><td colSpan="6" className="py-10 text-center text-slate-400 text-sm">Nenhum contrato ainda. <Link to="/garantia" className="text-[var(--emerald)] underline">Criar contratação</Link></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, tone, testId }) {
  const toneColor = tone === "danger" ? "#DC2626" : tone === "success" ? "#059669" : "#1E2A44";
  return (
    <div className="card-flat p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="eyebrow">{label}</div>
        <Icon size={18} weight="duotone" color={toneColor} />
      </div>
      <div className="metric-value text-3xl mt-3" style={{ color: toneColor }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
